import * as React from "react";
import {
  Building2,
  ClipboardList,
  Eye,
  FileText,
  FileUp,
  LogOut,
  Menu,
  PanelRightOpen,
  Plus,
  RefreshCcw,
  SendHorizontal,
  Settings,
  Share2,
  ShieldCheck,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ExportDialog } from "@/components/chat/ExportDialog";
import { MessageBubble, type Message } from "@/components/chat/MessageBubble";
import { SourceCard } from "@/components/chat/SourceCard";
import {
  SourceDocumentLightbox,
  type DocumentDownloadLoader,
  type DocumentPreviewLoader,
} from "@/components/chat/SourceDocumentLightbox";
import { DDQUploadDialog } from "@/components/ddq/UploadDialog";
import { DDQReviewDialog } from "@/components/ddq/ReviewDialog";
import { DDQProgressView } from "@/components/ddq/ProgressView";
import { DDQAccordionRenderer } from "@/components/ddq/AccordionRenderer";
import { DDQExportFilledButtons } from "@/components/ddq/ExportFilledButtons";
import { cn } from "@/lib/utils";
import { isServerMessageId } from "@/components/chat/FeedbackWidget";
import { useDdqRun } from "@/hooks/useDdqRun";
import { applyStep, finalizeSteps, type UIStep } from "@/lib/chat-steps";
import {
  aggregateDdqSources,
  askDdqFollowup,
  clearSavedDraft,
  getConversation,
  getConversations,
  getMe,
  logout,
  readSavedDraft,
  regenerateDdqQuestion,
  registerDraftProvider,
  setImpersonationTier,
  streamChat,
  type ChatBinding,
  type ChatSource,
  type ConversationSummary,
  type DDQAnsweredQuestion,
  type DDQFollowup,
  type DDQStructuredPayload,
  type DDQUploadResponse,
  type Fund,
} from "@/lib/api";
import type { TrustTrace } from "@/lib/trust-trace";
import { normalizeTrace } from "@/lib/trust-trace";

const FUND_STORAGE_KEY = "investor-qa.selected-fund";

function BrandMark() {
  return (
    <div className="flex items-center gap-3">
      <div className="grid size-9 place-items-center rounded-md bg-brand text-sm font-bold text-white">
        GP
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold leading-5">LP Due Diligence</div>
        <div className="text-xs text-muted-foreground">GreenPoint Partners</div>
      </div>
    </div>
  );
}

function relativeTime(dateStr: string): string {
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function ConversationRail({
  conversations,
  activeId,
  onSelect,
  onNew,
  onUploadDDQ,
  ddqUploadEnabled,
  embedded = false,
  drawerMode = false,
}: {
  conversations: ConversationSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onUploadDDQ?: () => void;
  ddqUploadEnabled?: boolean;
  /** When mounted inside the suite shell, the GlobalNav owns brand + global
   *  nav/identity, so the rail hides its own brand block and Admin link. */
  embedded?: boolean;
  /** When rendered in the mobile drawer, suppress the brand and admin link. */
  drawerMode?: boolean;
}) {
  return (
    <aside className="scenario-rail flex min-h-0 flex-col border-r bg-sidebar text-sidebar-foreground">
      {/* The shell's GlobalNav owns the brand wordmark when embedded, and the
          drawer header owns it when in drawer mode. */}
      {embedded || drawerMode ? null : (
        <div className="scenario-brand border-b border-sidebar-border p-4">
          <BrandMark />
        </div>
      )}

      <div className="scenario-meta flex items-center justify-between px-4 py-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Conversations
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {conversations.length} recent
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onNew} aria-label="New chat">
          <Plus />
          <span className="button-label">New</span>
        </Button>
      </div>

      {ddqUploadEnabled && onUploadDDQ ? (
        <div className="px-4 pb-3">
          <Button
            type="button"
            variant="subtle"
            size="sm"
            onClick={onUploadDDQ}
            aria-label="Upload DDQ"
            className="w-full justify-center"
          >
            <FileUp />
            Upload DDQ
          </Button>
        </div>
      ) : null}

      <div className="scenario-list min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {conversations.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            No conversations yet. Ask a question to start.
          </div>
        ) : (
          conversations.map((conv) => {
            const active = conv.id === activeId;
            const isDDQ = conv.kind === "ddq";
            const Icon = isDDQ ? ClipboardList : FileText;
            return (
              <button
                key={conv.id}
                type="button"
                onClick={() => onSelect(conv.id)}
                className={cn(
                  "scenario-button w-full rounded-md border p-3 text-left transition-colors",
                  active
                    ? "border-primary bg-accent text-accent-foreground"
                    : "border-transparent bg-transparent hover:bg-sidebar-accent",
                )}
                aria-pressed={active}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      "mt-0.5 grid size-7 shrink-0 place-items-center rounded-md",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-muted-foreground",
                    )}
                  >
                    <Icon className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <div className="line-clamp-2 text-sm font-medium leading-5">
                        {conv.title}
                      </div>
                      {isDDQ ? (
                        <Badge
                          variant="outline"
                          className="shrink-0 border-brand/40 bg-brand/5 px-1.5 py-0 font-mono text-[9px] uppercase tracking-wider text-brand"
                        >
                          DDQ
                        </Badge>
                      ) : null}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {relativeTime(conv.updated_at)}
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Admin link — only in the standalone chat page. In the suite shell the
          GlobalNav's Admin tab owns navigation to the admin product, so the
          rail drops this link to avoid a duplicate (and a hard nav off the
          single-page suite). The drawer also drops it. /v1/admin/* still requires gp_team role
          server-side, so an LP who reaches admin just sees error states. */}
      {embedded || drawerMode ? null : (
        <div className="border-t border-sidebar-border p-3">
          <a
            href="/suite.html?product=admin"
            className="inline-flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          >
            <Settings className="size-4" />
            <span>Admin portal</span>
          </a>
        </div>
      )}
    </aside>
  );
}

function FundSwitcher({
  funds,
  activeFundId,
  onChange,
  busy,
}: {
  funds: Fund[];
  activeFundId: string | null;
  onChange: (fundId: string) => void;
  busy?: boolean;
}) {
  if (funds.length < 2) return null;
  return (
    <div
      role="tablist"
      aria-label="Select fund"
      className="fund-switcher inline-flex items-center gap-1 rounded-md border bg-secondary p-1"
    >
      {funds.map((f) => {
        const active = f.id === activeFundId;
        return (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={busy && !active}
            onClick={() => {
              if (!active) onChange(f.id);
            }}
            className={cn(
              "rounded-sm px-3 py-1.5 text-sm font-medium transition-colors",
              active
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {f.name}
          </button>
        );
      })}
    </div>
  );
}

function ChatHeader({
  title,
  subtitle,
  funds,
  activeFundId,
  onFundChange,
  fundSwitchBusy,
  onOpenPanel,
  onOpenConversations,
  onNew,
  onExport,
  onLogout,
  showImpersonateToggle,
  impersonating,
  onToggleImpersonate,
}: {
  title: string;
  subtitle?: string | null;
  funds: Fund[];
  activeFundId: string | null;
  onFundChange: (fundId: string) => void;
  fundSwitchBusy?: boolean;
  onOpenPanel: () => void;
  onOpenConversations: () => void;
  onNew: () => void;
  onExport: (() => void) | null;
  onLogout: () => void;
  showImpersonateToggle?: boolean;
  impersonating?: boolean;
  onToggleImpersonate?: () => void;
}) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-3 border-b bg-card px-5 py-4">
      <div className="min-w-0 flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onOpenConversations}
          aria-label="Open conversations"
          className="conversations-drawer-trigger"
        >
          <Menu />
        </Button>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Building2 className="size-4" />
            {subtitle || "Investor diligence workspace"}
          </div>
          <h1 className="mt-1 truncate text-lg font-semibold">{title}</h1>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <FundSwitcher
          funds={funds}
          activeFundId={activeFundId}
          onChange={onFundChange}
          busy={fundSwitchBusy}
        />
        {showImpersonateToggle ? (
          <Button
            type="button"
            variant={impersonating ? "default" : "outline"}
            size="sm"
            onClick={onToggleImpersonate}
            aria-pressed={impersonating}
            aria-label={impersonating ? "Exit Standard-LP preview" : "Preview as a Standard LP"}
            title={
              impersonating
                ? "Exit Standard-LP preview — back to full GP access"
                : "Preview the chat as a Standard LP sees it (gated docs hidden)"
            }
          >
            <Eye />
            <span className="button-label">{impersonating ? "Viewing as LP" : "View as LP"}</span>
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onOpenPanel}
          aria-label="Open sources"
          title="Open sources"
          className="panel-toggle"
        >
          <PanelRightOpen />
          <span className="button-label">Sources</span>
        </Button>
        {onExport ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onExport}
            aria-label="Export conversation"
            title="Export conversation"
          >
            <Share2 />
            <span className="button-label">Export</span>
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onNew}
          aria-label="New chat"
          title="New chat"
        >
          <RefreshCcw />
          <span className="button-label">New chat</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onLogout}
          aria-label="Log out"
          title="Log out"
        >
          <LogOut />
          <span className="button-label">Log out</span>
        </Button>
      </div>
    </header>
  );
}

function SourcePanel({
  sources,
  activeIndex,
  open,
  onClose,
  onPreviewSource,
}: {
  sources: ChatSource[];
  activeIndex: number | null;
  open: boolean;
  onClose: () => void;
  onPreviewSource: (source: ChatSource) => void;
}) {
  return (
    <aside
      aria-label="Source documents"
      data-testid="uat-panel"
      className="uat-panel flex min-h-0 flex-col border-l bg-card"
      data-open={open ? "true" : "false"}
    >
      <div className="border-b p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-4 text-brand" />
              <h2 className="text-sm font-semibold">Sources</h2>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Cited documents for this answer.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close source panel"
            onClick={onClose}
            className="panel-close"
          >
            <X />
          </Button>
        </div>
      </div>

      <div className="uat-panel-scroll min-h-0 flex-1 overflow-y-auto p-4">
        {sources.length === 0 ? (
          <div className="rounded-md border border-dashed bg-background p-6 text-center text-xs text-muted-foreground">
            Sources will appear here as you chat.
          </div>
        ) : (
          <>
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Cited
            </div>
            <Separator className="my-3" />
            <div className="source-card-list">
              {sources.map((s) => (
                <SourceCard
                  key={s.index}
                  source={s}
                  highlighted={s.index === activeIndex}
                  onPreview={onPreviewSource}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

export type ChatPageProps = {
  initialConversations?: ConversationSummary[];
  initialConversationId?: string | null;
  initialMessages?: Message[];
  initialSources?: ChatSource[];
  initialPreviewSource?: ChatSource | null;
  disableConversationRefresh?: boolean;
  documentPreviewLoader?: DocumentPreviewLoader;
  documentDownloadLoader?: DocumentDownloadLoader;
  /**
   * Render inside the unified suite shell. When true, the conversation rail
   * drops its brand block + Admin-portal link (the shell GlobalNav owns global
   * nav/identity). All chat / DDQ / streaming behavior is unchanged. Defaults
   * to false so the standalone index.html chat page is untouched.
   */
  embedded?: boolean;
  /**
   * WS6 — lifted callback for the shell's Trust Console. Fires with the live
   * per-answer trace when a `trace` SSE event lands (WS4, gated by STREAM_STEPS)
   * and with `null` at the start of every new turn / new-chat / fund-switch so
   * the console falls back to EMPTY_TRACE. Optional: the standalone chat page
   * has no console and leaves this unwired (a pure no-op).
   */
  onTraceChange?: (trace: TrustTrace | null, bindings?: ChatBinding[]) => void;
  /**
   * WS6 — register the real `jumpToSource(index)` (open panel → scroll →
   * highlight the source card) up to the shell so the Trust Console's citation
   * chips route through the SAME logic the inline chat citations use, instead
   * of the shell's old getElementById DOM hack. Called once on mount with the
   * fn and once on unmount with null. Optional / unwired in the standalone page.
   */
  registerJumpToSource?: (fn: ((index: number) => void) | null) => void;
  /**
   * WS-T4 — callback to open the Trust Console. Wired by the shell (SuitePage)
   * when the TrustConsole is controlled there. Optional / unwired in the standalone
   * page or when the console is not controlled.
   */
  onOpenTrace?: () => void;
  /**
   * Workroom follow-up bridge: register the real `primeFollowup(question)` up to
   * the shell so the Workroom's "Flag" action can navigate to Q&A AND prefill the
   * chat input with context. Follows the same registerJumpToSource pattern —
   * called once on mount with the fn, once on unmount with null. Optional / unwired
   * in the standalone page.
   */
  registerPrimeFollowup?: (fn: ((question: string) => void) | null) => void;
};

export default function ChatPage({
  initialConversations = [],
  initialConversationId = null,
  initialMessages = [],
  initialSources = [],
  initialPreviewSource = null,
  disableConversationRefresh = false,
  documentPreviewLoader,
  documentDownloadLoader,
  embedded = false,
  onTraceChange,
  registerJumpToSource,
  onOpenTrace,
  registerPrimeFollowup,
}: ChatPageProps = {}) {
  const [conversations, setConversations] = React.useState<ConversationSummary[]>(initialConversations);
  const [activeConversationId, setActiveConversationId] = React.useState<string | null>(initialConversationId);
  const [messages, setMessages] = React.useState<Message[]>(initialMessages);
  const [sources, setSources] = React.useState<ChatSource[]>(initialSources);
  // WS5 — reasoning steps for the in-flight streaming answer (ephemeral, v1).
  // Only present when the worker's STREAM_STEPS flag is on; empty otherwise, so
  // the flag-OFF prod path is a visual no-op (MessageBubble falls back to the
  // typing-dots). `streamingAssistantId` is the STABLE local `assistant-<ts>`
  // id these steps belong to — kept separate from message.id, which reconciles
  // to the server UUID mid-stream on final-sources, so the strip stays attached
  // to the right bubble across that swap.
  const [steps, setSteps] = React.useState<UIStep[]>([]);
  const [streamingAssistantId, setStreamingAssistantId] = React.useState<string | null>(null);
  // WS6 — the live per-answer TrustConsole trace + bindings for the LAST
  // answer. `trace` is only populated when the worker's STREAM_STEPS flag is on
  // (WS4 emits one `trace` event before [DONE]); with the flag off in prod it
  // stays null, so the shell's console falls back to EMPTY_TRACE. Bindings ride
  // the final-sources event and are surfaced in the console's Citations tab.
  const [lastTrace, setLastTrace] = React.useState<TrustTrace | null>(null);
  const [lastBindings, setLastBindings] = React.useState<ChatBinding[]>([]);
  // WS-T4 — per-message trace history. When a conversation is loaded, this map
  // is populated from each assistant message's structured_payload.trace (if any),
  // so the Reasoning affordance can show that message's trace when clicked.
  const tracesByMessageIdRef = React.useRef<Map<string, TrustTrace>>(new Map());
  // WS-T4 — refs bridging the SSE handlers: the final `trace` event lands AFTER
  // final-sources (and the post-monitor update lands after [DONE]), so the
  // message id and the latest trace must meet outside stale closures.
  const pendingTraceMessageIdRef = React.useRef<string | null>(null);
  const lastTraceRef = React.useRef<TrustTrace | null>(null);
  const [activeSourceIndex, setActiveSourceIndex] = React.useState<number | null>(null);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [conversationsDrawerOpen, setConversationsDrawerOpen] = React.useState(false);
  const [previewSource, setPreviewSource] = React.useState<ChatSource | null>(initialPreviewSource);
  const [error, setError] = React.useState<string | null>(null);
  const [exportOpen, setExportOpen] = React.useState(false);
  const [entitledFunds, setEntitledFunds] = React.useState<Fund[]>([]);
  const [activeFundId, setActiveFundId] = React.useState<string | null>(null);
  const [identityLoaded, setIdentityLoaded] = React.useState(false);
  // Real role (drives the GP-only "View as LP" toggle) + whether the session
  // is currently scoped down to a standard LP (drives the preview banner).
  const [role, setRole] = React.useState<"gp_team" | "lp" | null>(null);
  const [impersonating, setImpersonating] = React.useState(false);
  // DDQ-upload beta state. The DDQ run orchestration (phase machine + answer
  // stream + dual cancel) lives in the headless `useDdqRun` hook; ChatPage wires
  // the host callbacks below so the chat message list / source panel / active
  // conversation / error banner update EXACTLY as they did when this was inline.
  const [ddqEnabled, setDdqEnabled] = React.useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  // Mirror the live draft into a ref so the session-expiry backstop in api.ts
  // can read the latest value without re-registering on every keystroke.
  const inputValueRef = React.useRef("");
  React.useEffect(() => {
    inputValueRef.current = input;
  }, [input]);

  // Register the draft getter + restore any draft preserved across a prior
  // session-expiry bounce to /login (self-hosted auth). Cleared once restored.
  React.useEffect(() => {
    registerDraftProvider(() => inputValueRef.current);
    const saved = readSavedDraft();
    if (saved) {
      setInput(saved);
      clearSavedDraft();
    }
    return () => registerDraftProvider(null);
  }, []);

  // Log out: clear the server session + cookie, then go to the login page.
  // Drop the draft provider first so the resulting navigation doesn't stash a
  // draft the LP is deliberately abandoning.
  const onLogout = React.useCallback(async () => {
    registerDraftProvider(null);
    try {
      await logout();
    } catch {
      /* best-effort — always proceed to /login */
    }
    try {
      window.location.assign("/login");
    } catch {
      /* non-browser env */
    }
  }, []);

  // GP-only "View as Standard LP" toggle. Flips the persisted impersonation
  // flag (api.ts then injects X-Impersonate-Tier on every /v1 request) and
  // reloads so identity, fund tiers, DDQ availability, and the conversation
  // list are all re-fetched at the new scope — and the current thread can't
  // bleed extended-tier context into a standard-LP preview (or vice versa).
  const onToggleImpersonation = React.useCallback(() => {
    setImpersonationTier(impersonating ? null : "lp_standard");
    try {
      window.location.reload();
    } catch {
      /* non-browser env */
    }
  }, [impersonating]);

  // Load identity + fund entitlements on mount. Selecting a fund:
  //  1) If localStorage has a stored fund that the LP is still entitled to,
  //     use it; otherwise default to the first entitled fund.
  //  2) If the LP has no entitlements at all, leave activeFundId null and
  //     surface a friendly empty state (rendered below).
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMe();
        if (cancelled) return;
        const funds = me.entitled_funds || [];
        setEntitledFunds(funds);
        setDdqEnabled(Boolean(me.ddq_upload_enabled));
        setRole(me.role);
        setImpersonating(Boolean(me.impersonating));
        if (funds.length > 0) {
          let next = funds[0].id;
          try {
            const stored = window.localStorage.getItem(FUND_STORAGE_KEY);
            if (stored && funds.some((f) => f.id === stored)) next = stored;
          } catch (_) { /* localStorage unavailable */ }
          setActiveFundId(next);
        }
      } catch (err) {
        console.error("getMe failed", err);
      } finally {
        if (!cancelled) setIdentityLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshConversations = React.useCallback(async () => {
    if (disableConversationRefresh) return;
    if (!activeFundId) return;
    try {
      const res = await getConversations(30, activeFundId);
      setConversations(res.conversations || []);
    } catch (err) {
      // Conversations sidebar is non-blocking — don't surface to the user.
      console.error("loadConversations failed", err);
    }
  }, [disableConversationRefresh, activeFundId]);

  React.useEffect(() => {
    void refreshConversations();
  }, [refreshConversations]);

  // ─── DDQ run orchestration (headless hook) ───────────────────────────────
  // The phase machine, answer stream, and dual cancel all live in useDdqRun;
  // ChatPage keeps ownership of the chat message list / source panel / active
  // conversation / error banner and feeds them via these host callbacks, so the
  // observable ChatPage behavior is identical to when this ran inline.
  const ddq = useDdqRun({
    onConversationId: (convId, _subId, total, displayName) => {
      setActiveConversationId(convId);
      const userId = `ddq-user-${Date.now()}`;
      setMessages([
        {
          id: userId,
          role: "user",
          content: `Please answer the following DDQ: ${displayName} — ${total} question${total === 1 ? "" : "s"}.`,
        },
      ]);
    },
    onDone: ({ messageId, payload, results }) => {
      // Stamp the DDQ-response message. `payload.questions` is already sorted
      // into the LP's DDQ order by the hook; `results` keeps the SSE-arrival
      // order (used only for the flat text fallback, matching prior behavior).
      setMessages((cur) => [
        ...cur.filter((m) => m.id !== messageId),
        {
          id: messageId,
          role: "assistant",
          content: results
            .map((q) => `${q.section} · ${q.question}\n${q.answer}`)
            .join("\n\n"),
          kind: "ddq_response",
          structured_payload: payload,
        },
      ]);
      // Aggregate citations into the source panel — from the SAME ordered array
      // stamped into payload.questions + read by the accordion, via the shared
      // helper, so panel cards and inline chips carry identical global numbers.
      setSources(aggregateDdqSources(payload.questions));
      void refreshConversations();
    },
    onError: (message) => setError(message || "DDQ run failed"),
  });

  // Auto-scroll to the newest content. Use `block: "nearest"` (was "end") so a
  // rapidly-appending reasoning strip doesn't yank the viewport away from a
  // reader who has scrolled up mid-answer, and throttle so a fast burst of step
  // events (many appends in a few hundred ms) doesn't fire a scroll per event.
  const lastScrollRef = React.useRef(0);
  React.useEffect(() => {
    const nowTs = Date.now();
    if (nowTs - lastScrollRef.current < 120) return;
    lastScrollRef.current = nowTs;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, steps.length]);

  // WS6 — bubble the live trace + bindings up to the shell's Trust Console
  // whenever they change. `null` → the console shows EMPTY_TRACE ("awaiting
  // question"); a real trace → the console renders that answer's reasoning. A
  // no-op in the standalone page (no console → onTraceChange unwired).
  React.useEffect(() => {
    onTraceChange?.(lastTrace, lastBindings);
  }, [lastTrace, lastBindings, onTraceChange]);

  const startNewChat = React.useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setSources([]);
    setActiveSourceIndex(null);
    setPreviewSource(null);
    setInput("");
    setError(null);
    setPanelOpen(false);
    setSteps([]);
    setStreamingAssistantId(null);
    // WS6 — a fresh chat has no answer → the console returns to EMPTY_TRACE.
    setLastTrace(null);
    setLastBindings([]);
  }, []);

  // Switching funds resets the in-flight chat thread (each fund has its own
  // conversation list). The fund preference is persisted so the next visit
  // lands on whatever fund the LP was last using.
  const switchFund = React.useCallback((fundId: string) => {
    setActiveFundId(fundId);
    try {
      window.localStorage.setItem(FUND_STORAGE_KEY, fundId);
    } catch (_) { /* localStorage unavailable */ }
    setActiveConversationId(null);
    setMessages([]);
    setSources([]);
    setActiveSourceIndex(null);
    setPreviewSource(null);
    setError(null);
    setPanelOpen(false);
    setSteps([]);
    setStreamingAssistantId(null);
    // WS6 — switching funds clears the thread → the console returns to EMPTY_TRACE.
    setLastTrace(null);
    setLastBindings([]);
  }, []);

  const loadConversation = React.useCallback(async (id: string) => {
    try {
      setError(null);
      const data = await getConversation(id);
      setActiveConversationId(id);
      setMessages(
        data.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          sources: m.sources,
          kind: m.kind,
          structured_payload: m.structured_payload,
        })),
      );
      // WS-T4 — populate per-message trace history from persisted structured_payload.
      // Build a map of message_id → normalized trace for the Reasoning affordance.
      const traceMap = new Map<string, TrustTrace>();
      for (const msg of data.messages) {
        if (msg.role === "assistant" && msg.structured_payload) {
          const sp = msg.structured_payload as Record<string, unknown>;
          if (sp.trace) {
            traceMap.set(msg.id, normalizeTrace(sp.trace));
          }
        }
      }
      tracesByMessageIdRef.current = traceMap;

      // Surface the most recent assistant's sources in the side panel.
      // For DDQ-response messages, aggregate citations across questions.
      const lastAssistant = [...data.messages].reverse().find((m) => m.role === "assistant");
      if (lastAssistant?.kind === "ddq_response" && lastAssistant.structured_payload) {
        const payload = lastAssistant.structured_payload as DDQStructuredPayload;
        // Same helper the accordion uses → panel card numbers match the chips.
        setSources(aggregateDdqSources(payload.questions || []));
      } else {
        setSources(lastAssistant?.sources ?? []);
      }
      setActiveSourceIndex(null);
      setPreviewSource(null);
      setPanelOpen(false);
      // WS-T4 — show the most recent answer's trace (if any exists), else EMPTY_TRACE.
      if (lastAssistant && lastAssistant.id && traceMap.has(lastAssistant.id)) {
        setLastTrace(traceMap.get(lastAssistant.id) ?? null);
      } else {
        setLastTrace(null);
      }
      setLastBindings([]);
      // Leaving DDQ progress view if we navigate to a different conversation.
      if (ddq.phase === "progress") ddq.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load conversation");
    }
  }, [ddq]);

  // ─── DDQ upload flow (thin host wrappers over useDdqRun) ─────────────────
  // The gating (feature flag + fund guard) and the host-owned resets that must
  // happen alongside a run stay here; the phase machine + stream + dual cancel
  // are the hook's.
  const openDDQUpload = React.useCallback(() => {
    if (!ddqEnabled) return;
    if (!activeFundId) {
      setError("Please select a fund before uploading a DDQ.");
      return;
    }
    setError(null);
    ddq.openUpload();
  }, [ddqEnabled, activeFundId, ddq]);

  const closeDDQDialogs = React.useCallback(() => {
    ddq.reset();
  }, [ddq]);

  const onDDQUploaded = React.useCallback(
    (resp: DDQUploadResponse) => {
      ddq.onUploaded(resp);
    },
    [ddq],
  );

  const startDDQRun = React.useCallback(
    (selected: string[]) => {
      // Host-owned reset that accompanies a run start — the hook owns only the
      // DDQ-run state, so ChatPage clears its chat thread / panel / error here.
      setActiveConversationId(null);
      setMessages([]);
      setSources([]);
      setError(null);
      void ddq.startRun(selected);
    },
    [ddq],
  );

  const cancelDDQRun = React.useCallback(async () => {
    await ddq.cancelRun();
    void refreshConversations();
  }, [ddq, refreshConversations]);

  const submit = React.useCallback(async () => {
    const query = input.trim();
    if (!query || busy) return;

    setBusy(true);
    setError(null);
    // WS5 — reset the reasoning strip at the start of every ask. The stable
    // local assistant id is what the steps key to (NOT message.id, which
    // reconciles to the server UUID on final-sources).
    setSteps([]);
    // WS6 — clear the prior answer's trace/bindings so the console shows
    // EMPTY_TRACE while this new answer is in flight (the WS-T3 early snapshot,
    // if the STREAM_STEPS flag is on, repopulates it within ~1-2s via onTrace).
    setLastTrace(null);
    setLastBindings([]);
    lastTraceRef.current = null;
    pendingTraceMessageIdRef.current = null;

    const userId = `user-${Date.now()}`;
    const assistantId = `assistant-${Date.now()}`;
    setStreamingAssistantId(assistantId);
    const optimisticUser: Message = { id: userId, role: "user", content: query };
    // Append both bubbles up front: the user message and an empty assistant
    // bubble that fills in via streaming token deltas.
    setMessages((prev) => [
      ...prev,
      optimisticUser,
      { id: assistantId, role: "assistant", content: "", sources: [] },
    ]);
    setInput("");

    if (!activeFundId) {
      setError("Please select a fund before asking a question.");
      setBusy(false);
      // Re-instate the user input.
      setInput(query);
      setMessages((prev) => prev.filter((m) => m.id !== userId && m.id !== assistantId));
      setStreamingAssistantId(null);
      return;
    }

    // Fires on the first streamed token: mark every reasoning step done so the
    // strip settles before the prose starts filling in. Guard so it only runs
    // once per answer (later deltas are cheap no-ops via finalizeSteps).
    let sawFirstToken = false;

    try {
      const res = await streamChat(query, activeFundId, activeConversationId, {
        onConversationId: (id) => setActiveConversationId(id),
        // WS5 — reasoning step. Only fires when the worker's STREAM_STEPS flag
        // is on; otherwise this never runs and the strip stays empty (the
        // typing-dots fallback in MessageBubble is the flag-OFF prod path).
        onStep: (step) => setSteps((prev) => applyStep(prev, step)),
        onInitialSources: (initialSources) => {
          // First-pass (chunk-based) sources arrive before tokens. Populate
          // the side-panel state so the data is ready when the LP opens it,
          // but DO NOT auto-open the drawer at narrow viewports — that
          // takes the chat out of focus mid-answer. The Sources button in
          // the chat header (and any citation chip) opens the drawer when
          // the LP wants it. final-sources overwrites this once the answer
          // is done.
          setSources(initialSources);
        },
        onContent: (_delta, accumulated) => {
          // First token → the reasoning stages are done; settle the strip.
          if (!sawFirstToken) {
            sawFirstToken = true;
            setSteps((prev) => finalizeSteps(prev));
          }
          // Replace the empty assistant bubble's content with the rolling
          // accumulated answer. React batches; this stays smooth.
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: accumulated } : m)),
          );
        },
        onFinalSources: (finalSources, _citedTags, messageId) => {
          setSources(finalSources);
          // Reconcile the optimistic `assistant-<ts>` bubble to the real server
          // message UUID (when the worker supplies it on final-sources) so the
          // feedback widget renders on this freshly streamed answer, not only
          // after a conversation reload.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, id: messageId ?? m.id, sources: finalSources } : m,
            ),
          );
          // WS5 — the steps are keyed to the STABLE local assistant id, but the
          // bubble's id just reconciled to the server UUID. Re-point the
          // steps-owner id at the reconciled id so the collapsed "Reasoning"
          // disclosure stays attached to this same bubble across the swap.
          if (messageId) setStreamingAssistantId(messageId);
          setActiveSourceIndex(null);
          // WS-T4 — remember the real message_id so the trace events that follow
          // (final pre-[DONE] + post-monitor update) key into the history map.
          if (messageId) {
            pendingTraceMessageIdRef.current = messageId;
            const current = lastTraceRef.current;
            if (current) tracesByMessageIdRef.current.set(messageId, current);
          }
        },
        // WS6 — machine-readable {entity, attribute, value, source} claims ride
        // the final-sources event. Surface them in the Trust Console's Citations
        // tab (previously dropped by the client).
        onBindings: (bindings) => setLastBindings(bindings),
        // WS6 — the single per-answer TrustConsole trace (WS4 emits one before
        // [DONE], only when STREAM_STEPS is on). Store it so the shell's console
        // renders THIS answer's reasoning; with the flag off this never fires and
        // the console stays on EMPTY_TRACE.
        // WS-T4 — also store in the per-message map (keyed by the real message_id
        // once final-sources supplied it) so the Reasoning affordance can replay it.
        onTrace: (trace) => {
          lastTraceRef.current = trace;
          setLastTrace(trace);
          const mid = pendingTraceMessageIdRef.current;
          if (mid) tracesByMessageIdRef.current.set(mid, trace);
        },
      });
      // If the worker returned no resolved sources but did produce an answer,
      // keep whatever side-panel state we already have (initial chunks).
      if (!res.answer) {
        throw new Error("Empty response from chat stream");
      }
      void refreshConversations();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chat request failed");
      // Re-instate the user input so they can retry without retyping.
      setInput(query);
      // Drop both optimistic bubbles on error.
      setMessages((prev) => prev.filter((m) => m.id !== userId && m.id !== assistantId));
      // WS5 — clear the reasoning strip; its bubble is gone.
      setSteps([]);
      setStreamingAssistantId(null);
      // WS6 — the answer failed; drop any partial trace/bindings so the console
      // returns to EMPTY_TRACE rather than showing a half-formed trace.
      setLastTrace(null);
      setLastBindings([]);
      lastTraceRef.current = null;
      pendingTraceMessageIdRef.current = null;
    } finally {
      // `busy` → false flips the strip from live to the collapsed "Reasoning"
      // disclosure (see the `streaming` prop wiring below); the steps state is
      // deliberately kept so the disclosure has something to show.
      setBusy(false);
    }
  }, [input, busy, activeConversationId, activeFundId, refreshConversations]);

  const jumpToSource = React.useCallback((index: number) => {
    setActiveSourceIndex(index);
    setPanelOpen(true);
    requestAnimationFrame(() => {
      document.getElementById(`source-${index}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, []);

  // WS6 — register the real jumpToSource up to the shell so the Trust Console's
  // citation chips open + scroll + highlight the SAME source card as the inline
  // chat citations (replacing the shell's old getElementById DOM hack). Stable
  // (empty-dep useCallback), so this registers once on mount / clears on unmount.
  React.useEffect(() => {
    registerJumpToSource?.(jumpToSource);
    return () => registerJumpToSource?.(null);
  }, [registerJumpToSource, jumpToSource]);

  // Workroom follow-up bridge — register a stable fn that primes the chat input
  // with a flagged-question context prefix, mirroring askDDQFollowup's behaviour
  // but accepting a plain question string instead of a DDQAnsweredQuestion.
  const primeFollowup = React.useCallback((question: string) => {
    const primed = `Re: ${question}\n\n`;
    setInput(primed);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch { /* ignored */ }
      el.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  }, []);

  React.useEffect(() => {
    registerPrimeFollowup?.(primeFollowup);
    return () => registerPrimeFollowup?.(null);
  }, [registerPrimeFollowup, primeFollowup]);

  // WS-T4 — show a message's trace in the console. Called when the Reasoning
  // button is clicked on an assistant message. Loads that message's trace and
  // opens the console.
  const onShowTrace = React.useCallback(
    (messageId: string) => {
      const trace = tracesByMessageIdRef.current.get(messageId);
      if (trace) {
        setLastTrace(trace);
        onOpenTrace?.();
      }
    },
    [onOpenTrace],
  );

  // Inline DDQ follow-up (#17): the typed question goes through the EXISTING
  // /v1/chat/sync path with light DDQ-context prefixing (see askDdqFollowup) —
  // serving, gating, and verification are untouched chat/sync. The follow-up's
  // sources fold into the side panel: docs the DDQ already cites keep their
  // global number; new docs append AFTER the current max so the DDQ chips'
  // numbering never shifts. The returned follow-up carries panel-renumbered
  // sources so its inline chips match the panel cards.
  const submitDDQFollowup = React.useCallback(
    async (q: DDQAnsweredQuestion, text: string): Promise<DDQFollowup> => {
      if (!activeFundId) throw new Error("Please select a fund before asking a follow-up.");
      const f = await askDdqFollowup(q, text, activeFundId, activeConversationId);
      const indexByKey = new Map<string, number>(
        sources.map((s) => [(s.tag || s.document_name || "").toLowerCase(), s.index] as const),
      );
      let nextIdx = sources.reduce((max, s) => Math.max(max, s.index), 0);
      const additions: ChatSource[] = [];
      const remapped = f.sources.map((s) => {
        const key = (s.tag || s.document_name || "").toLowerCase();
        const existing = key ? indexByKey.get(key) : undefined;
        if (existing !== undefined) return { ...s, index: existing };
        nextIdx += 1;
        if (key) indexByKey.set(key, nextIdx);
        const added = { ...s, index: nextIdx };
        additions.push(added);
        return added;
      });
      if (additions.length) setSources((prev) => [...prev, ...additions]);
      return { ...f, sources: remapped };
    },
    [activeFundId, activeConversationId, sources],
  );

  // Per-question re-run (#14): re-executes ONE question server-side (same DDQ
  // machinery + gates), then swaps the fresh answer into the message's
  // structured payload and re-aggregates the source panel so chip numbers
  // stay in lockstep with the cards.
  const rerunDDQQuestion = React.useCallback(
    async (
      messageId: string,
      payload: DDQStructuredPayload,
      q: DDQAnsweredQuestion,
    ): Promise<DDQAnsweredQuestion> => {
      const res = await regenerateDdqQuestion(payload.submission_id, q.question_id);
      const nextQuestions = (payload.questions || []).map((x) =>
        x.question_id === q.question_id ? res.question : x,
      );
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, structured_payload: { ...payload, questions: nextQuestions } }
            : m,
        ),
      );
      setSources(aggregateDdqSources(nextQuestions));
      return res.question;
    },
    [],
  );

  // Prime a follow-up question in the chat input + focus the textarea so
  // the LP types directly into a context-loaded prompt. Used from the DDQ
  // accordion 'Ask follow-up' button.
  const askDDQFollowup = React.useCallback((q: DDQAnsweredQuestion) => {
    const primed = `Re: ${q.section} / ${q.question}\n\n`;
    setInput(primed);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      // Park the cursor at the end so the LP types right after the prime.
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch { /* ignored */ }
      el.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  }, []);

  const activeFund = React.useMemo(
    () => entitledFunds.find((f) => f.id === activeFundId) || null,
    [entitledFunds, activeFundId],
  );

  // #145 — display name for the golden's `vehicle_label`, and the id of the
  // newest assistant bubble (the only one whose live bindings we still hold).
  const activeFundName = activeFund?.name ?? null;
  const lastAssistantMessageId = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return messages[i].id;
    }
    return null;
  }, [messages]);

  const headerTitle =
    messages.length === 0
      ? activeFund
        ? `Ask a question about ${activeFund.name}`
        : "Ask a diligence question"
      : conversations.find((c) => c.id === activeConversationId)?.title ||
        messages.find((m) => m.role === "user")?.content.slice(0, 80) ||
        "Conversation";

  return (
    <div
      className="workspace-shell bg-background text-foreground"
      // When embedded in the suite shell, fill the flex slot (the shell owns
      // the GlobalNav + the reserved Trust Console strip space) instead of the
      // full viewport height that the standalone page uses.
      data-embedded={embedded ? "true" : undefined}
    >
      <button
        type="button"
        aria-label="Close source panel backdrop"
        className="panel-backdrop"
        data-open={panelOpen ? "true" : "false"}
        onClick={() => setPanelOpen(false)}
      />

      {/* Conversations drawer backdrop and panel — mobile only (≤768px) */}
      <div
        className={cn(
          "conversations-drawer-backdrop fixed inset-0 z-[40] bg-[rgb(26_32_44_/_0.24)] transition-opacity duration-200",
          conversationsDrawerOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={() => setConversationsDrawerOpen(false)}
        aria-hidden={!conversationsDrawerOpen}
      />
      <aside
        className={cn(
          "conversations-drawer fixed left-0 top-0 z-[41] flex h-full w-[min(280px,calc(100vw-40px))] flex-col bg-sidebar shadow-[var(--shadow-lg)] transition-transform duration-200 ease-out",
          conversationsDrawerOpen ? "translate-x-0" : "-translate-x-full",
        )}
        aria-label="Conversations"
        aria-hidden={!conversationsDrawerOpen}
      >
        <div className="flex items-center justify-between border-b border-sidebar-border px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Conversations
          </span>
          <button
            type="button"
            onClick={() => setConversationsDrawerOpen(false)}
            className="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label="Close conversations"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <ConversationRail
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={(id) => {
              loadConversation(id);
              setConversationsDrawerOpen(false);
            }}
            onNew={() => {
              startNewChat();
              setConversationsDrawerOpen(false);
            }}
            onUploadDDQ={openDDQUpload}
            ddqUploadEnabled={ddqEnabled && Boolean(activeFundId)}
            embedded={embedded}
            drawerMode={true}
          />
        </div>
      </aside>

      <ConversationRail
        conversations={conversations}
        activeId={activeConversationId}
        onSelect={loadConversation}
        onNew={startNewChat}
        onUploadDDQ={openDDQUpload}
        ddqUploadEnabled={ddqEnabled && Boolean(activeFundId)}
        embedded={embedded}
      />

      <main className="chat-pane flex min-h-0 min-w-0 flex-col">
        {impersonating ? (
          <div
            role="status"
            className="flex flex-wrap items-center justify-between gap-3 border-b border-warning/40 bg-warning-soft px-5 py-2 text-sm text-warning"
          >
            <span className="flex items-center gap-2 font-medium">
              <Eye className="size-4 shrink-0" />
              Previewing as a Standard LP — gated documents (board minutes, employment
              agreements, MIP grants, litigation) are hidden, exactly as an external LP sees.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onToggleImpersonation}
              className="bg-card"
            >
              Exit preview
            </Button>
          </div>
        ) : null}
        <ChatHeader
          title={headerTitle}
          subtitle={activeFund?.name ? `${activeFund.name} diligence workspace` : null}
          funds={entitledFunds}
          activeFundId={activeFundId}
          onFundChange={switchFund}
          fundSwitchBusy={busy}
          onOpenPanel={() => setPanelOpen(true)}
          onOpenConversations={() => setConversationsDrawerOpen(true)}
          onNew={startNewChat}
          // Export only renders when there's an active conversation with
          // at least one persisted message — nothing to export from an
          // empty new-chat state.
          onExport={
            activeConversationId && messages.length > 0
              ? () => setExportOpen(true)
              : null
          }
          onLogout={onLogout}
          showImpersonateToggle={role === "gp_team"}
          impersonating={impersonating}
          onToggleImpersonate={onToggleImpersonation}
        />

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
          <div className="mx-auto flex max-w-3xl flex-col gap-5">
            {ddq.phase === "progress" && ddq.upload ? (
              <DDQProgressView
                displayName={ddq.upload.display_name}
                fundName={activeFund?.name || ddq.upload.fund_id}
                total={ddq.total}
                answered={ddq.results.length}
                results={ddq.results}
                retrievalDone={ddq.retrievalDone}
                onCancel={cancelDDQRun}
                cancelling={ddq.cancelling}
                parsedStructure={ddq.upload.parsed_questions}
              />
            ) : messages.length === 0 ? (
              identityLoaded && entitledFunds.length === 0 ? (
                <div className="rounded-md border border-dashed bg-card p-8 text-center">
                  <Badge variant="outline" className="mx-auto">
                    No fund access
                  </Badge>
                  <h2 className="mt-3 text-lg font-semibold">
                    Your access has not been provisioned for any GreenPoint fund yet
                  </h2>
                  <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
                    Please contact <a className="underline" href="mailto:ir@greenpointpartners.com">ir@greenpointpartners.com</a> for an
                    invitation to the Outpost A-2 or GRAF II data rooms.
                  </p>
                </div>
              ) : (
                <div className="rounded-md border bg-card p-8 text-center">
                  <Badge variant="success" className="mx-auto">
                    Ready
                  </Badge>
                  <h2 className="mt-3 text-lg font-semibold">
                    Ask a diligence question about {activeFund?.name || "the selected fund"}
                  </h2>
                  <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
                    Answers cite source documents from the Q2 2026 data room.
                    {ddqEnabled ? (
                      <> Or use <strong>Upload DDQ</strong> in the conversation rail to pre-answer a full questionnaire.</>
                    ) : null}
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {(activeFundId === "graf_ii"
                      ? [
                          "What is GRAF II's target fund size and return profile?",
                          "Summarize the management fee and carried interest terms.",
                          "Walk me through GRAF I's track record across Outpost, Lysara and One Living.",
                        ]
                      : [
                          "What is the platform yield-on-cost?",
                          "Summarize the management incentive plan structure.",
                          "Walk me through Outpost's customer concentration.",
                        ]
                    ).map((q) => (
                      <Button
                        key={q}
                        variant="subtle"
                        size="sm"
                        type="button"
                        onClick={() => setInput(q)}
                      >
                        {q}
                      </Button>
                    ))}
                  </div>
                </div>
              )
            ) : (
              messages.map((m, mi) => {
                if (m.kind === "ddq_response" && m.structured_payload) {
                  const payload = m.structured_payload as DDQStructuredPayload;
                  return (
                    <section key={m.id} className="rounded-md border bg-card p-4 shadow-sm">
                      <header className="mb-3 flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">DDQ response</Badge>
                        <span className="text-sm font-semibold">
                          {payload.display_name}
                        </span>
                        <Badge variant="outline">
                          {payload.question_count} answers
                        </Badge>
                        {payload.cancelled ? (
                          <Badge variant="outline" className="border-warning/50 bg-warning-soft text-warning">
                            Cancelled
                          </Badge>
                        ) : null}
                        {payload.submission_id ? (
                          <div className="ml-auto">
                            <DDQExportFilledButtons submissionId={payload.submission_id} />
                          </div>
                        ) : null}
                      </header>
                      <DDQAccordionRenderer
                        messageId={m.id}
                        questions={payload.questions || []}
                        // Keep section order locked to the LP's DDQ
                        // structure even if `payload.questions` got
                        // built from arrival-order state somewhere.
                        // Only available for the run currently in
                        // ChatPage scope; reloaded conversations rely
                        // on the server-persisted parsed-order array.
                        parsedStructure={
                          ddq.upload?.submission_id === payload.submission_id
                            ? ddq.upload.parsed_questions
                            : null
                        }
                        onCitationClick={(src) => {
                          const idx = sources.findIndex(
                            (s) =>
                              (s.tag && src.tag && s.tag === src.tag) ||
                              s.document_id === src.document_id,
                          );
                          if (idx >= 0) jumpToSource(sources[idx].index);
                        }}
                        onAskFollowup={askDDQFollowup}
                        onFollowup={submitDDQFollowup}
                        // Re-run needs the persisted submission — gate on the
                        // server message UUID (optimistic ids can't re-run).
                        onRerunQuestion={
                          isServerMessageId(m.id)
                            ? (q) => rerunDDQQuestion(m.id, payload, q)
                            : undefined
                        }
                      />
                    </section>
                  );
                }
                // WS5 — the reasoning strip belongs to exactly ONE bubble: the
                // assistant answer these steps were streamed for. Match on the
                // steps-owner id (which follows the local→UUID reconcile), and
                // only when there are steps to show (flag-OFF ⇒ empty ⇒ the
                // bubble's typing-dots fallback is untouched). `streaming` is
                // true only while the request is in flight; once it settles the
                // strip collapses to the "Reasoning" disclosure.
                const isStepOwner = steps.length > 0 && m.id === streamingAssistantId;
                // WS-T4 — check if this message has a persisted trace.
                const hasTrace = tracesByMessageIdRef.current.has(m.id);
                // #145 — the nuance composer's prefill inputs. The QUESTION is
                // the user turn this answer replied to (it is the golden's
                // identity, so it must be the real preceding turn, not the
                // thread title). BINDINGS ride the `final-sources` event and are
                // only held for the most recent answer, so older bubbles capture
                // with an empty bindings array rather than a wrong one.
                const precedingQuestion =
                  m.role === "assistant"
                    ? messages
                        .slice(0, mi)
                        .reverse()
                        .find((p) => p.role === "user")?.content ?? null
                    : null;
                const isNewestAssistant = m.id === lastAssistantMessageId;
                return (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    sources={sources}
                    onCitationClick={jumpToSource}
                    steps={isStepOwner ? steps : []}
                    streaming={isStepOwner ? busy : false}
                    hasTrace={hasTrace}
                    onShowTrace={hasTrace ? () => onShowTrace(m.id) : undefined}
                    role={role}
                    fund={activeFundId}
                    fundName={activeFundName}
                    question={precedingQuestion}
                    bindings={isNewestAssistant ? lastBindings : undefined}
                  />
                );
              })
            )}

            {error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="border-t bg-background px-5 py-4">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-end gap-3 rounded-md border bg-card p-2 shadow-sm">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void submit();
                  }
                }}
                placeholder="Ask a diligence question…"
                className="min-h-12 resize-none border-0 shadow-none focus-visible:ring-0"
                disabled={busy}
              />
              <Button
                type="button"
                size="icon"
                aria-label="Send question"
                onClick={() => void submit()}
                disabled={!input.trim() || busy}
              >
                <SendHorizontal />
              </Button>
            </div>
            <div className="mt-2 text-center text-xs text-muted-foreground">
              Answers are grounded in GreenPoint's Q2 2026 data room and cite their sources.
            </div>
          </div>
        </div>
      </main>

      <SourcePanel
        sources={sources}
        activeIndex={activeSourceIndex}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onPreviewSource={setPreviewSource}
      />

      <SourceDocumentLightbox
        source={previewSource}
        open={previewSource !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPreviewSource(null);
        }}
        loadPreview={documentPreviewLoader}
        loadDownload={documentDownloadLoader}
      />

      <ExportDialog
        conversationId={activeConversationId}
        open={exportOpen}
        onClose={() => setExportOpen(false)}
      />

      <DDQUploadDialog
        open={ddq.phase === "upload"}
        fund={activeFund}
        onClose={closeDDQDialogs}
        onUploaded={onDDQUploaded}
      />

      <DDQReviewDialog
        open={ddq.phase === "review"}
        displayName={ddq.upload?.display_name || ""}
        parsed={ddq.upload?.parsed_questions || null}
        fundName={activeFund?.name || ddq.upload?.fund_id || ""}
        onCancel={closeDDQDialogs}
        onConfirm={(ids) => startDDQRun(ids)}
      />
    </div>
  );
}
